import { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';
import { getManagedOrderSizes, updateOrderSizes } from '@/api/orderSizes';
import { refreshOrderSizesResult } from '@/hooks/useOrderSizes';

// A replacement affects this tenant/source even if the initiating form or
// administrator leaves. These are observed in-memory obligations, not receipts
// or a server lock. No credentials or drafts are stored in this registry.
const resources = new Map();
let rowSequence = 0;
const connection = () => JSON.stringify([window.lvbDesktop?.backendUrl, window.lvbDesktop?.localBackendUrl]);
const identity = binding => JSON.stringify([binding?.user?.tenant_id, binding?.user?.id, binding?.source]);
const resourceFor = key => {
  if (!resources.has(key)) resources.set(key, { epoch: 0, write: null, refreshRequired: false, observers: new Set() });
  return resources.get(key);
};
const publish = (resource, origin) => resource.observers.forEach(observer => observer(origin));
const normalize = rows => rows.map(({ label, archived }) => ({ label: label.trim(), archived }));
export const validSizeList = rows => Array.isArray(rows) && rows.length > 0
  && rows.some(row => row?.archived === false)
  && rows.every(row => row && typeof row.label === 'string' && !!row.label.trim()
    && Array.from(row.label.trim()).length <= 40 && typeof row.archived === 'boolean')
  && new Set(rows.map(row => row.label.trim().toLowerCase())).size === rows.length;
const managedRows = value => validSizeList(value?.sizes)
  && value.sizes.every(row => row.label === row.label.trim() && typeof row.in_use === 'boolean')
  ? value.sizes.map(({ label, archived, in_use }) => ({ label, archived, in_use })) : null;
const samePayload = (value, submitted) => Array.isArray(value?.sizes) && value.sizes.length === submitted.length
  && value.sizes.every((row, index) => row?.label === submitted[index].label && row.archived === submitted[index].archived);
const draftRows = rows => rows.map(row => ({ ...row, id: `size-${++rowSequence}`, originLabel: row.label }));

function editorController(authorize, resource) {
  const owner = {};
  const listeners = new Set();
  let active = false;
  let version = 0;
  let draftRevision = 0;
  let request = null;
  let saving = false;
  let reviewEpoch = -1;
  let snapshot = { rows: null, baseline: null, review: null, reviewNeeded: false, loading: false,
    saving: false, active: false, pending: false, unknown: false, notice: '', readError: '', choicesError: false };
  const owns = () => active && !!authorize();
  const notify = () => {
    snapshot = { ...snapshot, active: owns(), pending: resource.write?.status === 'pending', unknown: resource.write?.status === 'unknown' };
    listeners.forEach(listener => listener());
  };
  const patch = update => { snapshot = { ...snapshot, ...update }; notify(); };
  const cancelRead = () => { ++version; request?.abort(); request = null; };
  const refresh = async ({ acceptedRevision = null } = {}) => {
    if (!owns() || request || resource.write?.status === 'pending') return false;
    const config = authorize();
    if (!config) return false;
    const controller = new AbortController();
    request = controller;
    const readVersion = ++version;
    const epoch = resource.epoch;
    const current = () => owns() && version === readVersion && resource.epoch === epoch;
    patch({ loading: true, readError: '', review: null });
    try {
      const rows = managedRows(await getManagedOrderSizes({ ...config, signal: controller.signal }));
      if (!rows) throw new Error('Unavailable size policy');
      if (!current()) return false;
      const adopt = snapshot.rows === null || (acceptedRevision !== null && draftRevision === acceptedRevision);
      const needsReview = resource.write?.status === 'unknown' || !adopt;
      reviewEpoch = epoch;
      patch({ ...(adopt ? { rows: draftRows(rows), baseline: rows } : {}), review: needsReview ? rows : null, reviewNeeded: needsReview });
      if (resource.refreshRequired || snapshot.choicesError) {
        const result = await refreshOrderSizesResult({ config: { ...config, signal: controller.signal }, isCurrent: current });
        if (!current()) return false;
        const fresh = result?.status === 'fresh';
        if (fresh) resource.refreshRequired = false;
        patch({ choicesError: !fresh });
      }
      return true;
    } catch {
      if (current()) patch({ readError: 'Current sizes could not be established. Your existing draft is retained.', reviewNeeded: snapshot.rows !== null, review: null });
      return false;
    } finally {
      if (request === controller) request = null;
      if (current()) patch({ loading: false });
    }
  };
  const changed = origin => {
    if (origin === owner) { notify(); return; }
    cancelRead();
    if (owns()) {
      patch({ loading: false, review: null, reviewNeeded: snapshot.rows !== null });
      if (resource.write?.status !== 'pending' && !saving) refresh();
    }
  };
  const protectedLabels = () => (snapshot.baseline || []).filter(row => row.in_use).map(row => row.label);
  const blockedReason = () => {
    if (!owns()) return 'This size editor no longer belongs to the active cloud administrator. Reopen Edit Sizes.';
    if (saving || request || resource.write?.status === 'pending') return 'A size request is still pending.';
    if (!snapshot.rows || snapshot.readError || snapshot.choicesError || snapshot.reviewNeeded || resource.write) return 'Refresh and review current sizes before saving.';
    if (!validSizeList(snapshot.rows)) return 'Use unique names of 1–40 characters and keep at least one active size.';
    const labels = normalize(snapshot.rows).map(row => row.label);
    if (protectedLabels().some(label => !labels.includes(label))) return 'Retain every current in-use size with its exact name. Archive it instead of renaming or removing it.';
    return '';
  };
  const edit = transform => {
    if (!owns() || !snapshot.rows) return;
    ++draftRevision;
    patch({ rows: transform(snapshot.rows) });
  };
  const save = async () => {
    if (blockedReason()) return;
    const config = authorize();
    if (!config) return;
    const submitted = normalize(snapshot.rows);
    const submittedOrigins = new Map(snapshot.rows.map((row, index) => [row.id, submitted[index].label]));
    const submittedRevision = draftRevision;
    const attempt = { status: 'pending' };
    saving = true;
    resource.write = attempt;
    ++resource.epoch;
    patch({ saving: true, notice: '', review: null });
    publish(resource, owner);
    let accepted = false;
    let refused = false;
    try {
      const response = await updateOrderSizes(submitted, config);
      accepted = samePayload(response, submitted);
    } catch (error) {
      const status = error?.response?.status;
      refused = [400, 401, 403, 404, 409, 422].includes(status);
    }
    if (resource.write === attempt) {
      resource.write = accepted || refused ? null : { status: 'unknown' };
      resource.refreshRequired = resource.refreshRequired || accepted || !refused;
      ++resource.epoch;
      publish(resource, owner);
    }
    if (owns()) {
      // A verified acknowledgement binds submitted labels to the captured row
      // identities. Later values and newly added rows remain untouched; no row
      // inherits protection merely by being renamed to another row's label.
      if (accepted) patch({ rows: snapshot.rows.map(row => submittedOrigins.has(row.id)
        ? { ...row, originLabel: submittedOrigins.get(row.id) } : row) });
      patch({ notice: accepted ? 'Sizes saved. Checking current size availability.' : refused
        ? 'The replacement was refused. Your draft is retained; refresh and review current sizes.'
        : 'The save outcome is unknown. Do not repeat it blindly. Read and review current sizes before a deliberate new replacement.',
      reviewNeeded: true });
      if (accepted) {
        await refresh({ acceptedRevision: submittedRevision });
        if (owns()) patch({ notice: 'Sizes saved.' });
      }
    }
    saving = false;
    if (owns()) patch({ saving: false });
  };
  return {
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
    getSnapshot: () => snapshot,
    activate() { active = true; resource.observers.add(changed); notify(); if (resource.write?.status !== 'pending') refresh(); },
    retire() { active = false; cancelRead(); resource.observers.delete(changed); notify(); },
    notify, refresh, save, blockedReason,
    add() { edit(rows => [...rows, { id: `size-${++rowSequence}`, label: '', archived: false, originLabel: null }]); },
    update(id, value) { edit(rows => rows.map(row => row.id !== id ? row : { ...row, ...value, ...(row.originLabel === row.label && protectedLabels().includes(row.label) ? { label: row.label } : {}) })); },
    remove(id) { edit(rows => rows.filter(row => row.id !== id || (row.originLabel === row.label && protectedLabels().includes(row.label)))); },
    review() {
      if (!owns() || saving || request || !snapshot.review || reviewEpoch !== resource.epoch || resource.write?.status === 'pending') return;
      // This approves a new replacement against an observation. It does not
      // identify the outcome of an earlier uncertain request or merge its rows.
      if (resource.write?.status === 'unknown') { resource.write = null; ++resource.epoch; publish(resource, owner); }
      patch({ baseline: snapshot.review, review: null, reviewNeeded: false,
        notice: 'Current sizes reviewed. Your draft is unchanged. A new Save is a deliberate replacement, not a retry receipt.' });
    },
  };
}

export function useSizeEditor(opening) {
  const { sessionBinding, getToken } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const source = connection();
  const principal = identity(sessionBinding);
  const roles = sessionBinding?.user?.roles || [sessionBinding?.user?.role];
  const permitted = roles.includes('admin') && !isDesktopOffline();
  const latest = useRef(null);
  latest.current = { sessionBinding, getToken, source, opening };
  const controllerRef = useRef(null);
  if (!controllerRef.current) {
    const capturedIdentity = identity(sessionBinding);
    const capturedRevision = revision;
    const capturedSource = source;
    const scope = JSON.stringify([sessionBinding?.user?.tenant_id, source]);
    const authorize = () => {
      const current = latest.current;
      const binding = current.sessionBinding;
      const roles = binding?.user?.roles || [binding?.user?.role];
      if (!binding?.user?.tenant_id || !binding?.user?.id || binding.source !== 'cloud' || !roles.includes('admin')
        || identity(binding) !== capturedIdentity || binding.revision !== capturedRevision
        || getActiveSessionRevision() !== capturedRevision || current.source !== capturedSource || connection() !== capturedSource
        || isDesktopOffline() || (current.opening && !current.opening.current())) return null;
      const token = current.getToken?.();
      return token ? { headers: { Authorization: `Bearer ${token}` }, timeout: 30000, suppressServerErrorNotification: true } : null;
    };
    controllerRef.current = editorController(authorize, resourceFor(scope));
  }
  const controller = controllerRef.current;
  useLayoutEffect(() => { controller.activate(); return () => controller.retire(); }, [controller]);
  useLayoutEffect(() => { controller.notify(); }, [controller, revision, sessionBinding, source, principal, permitted]);
  return { controller, ...useSyncExternalStore(controller.subscribe, controller.getSnapshot) };
}
